import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { DELETE_FEATURE, UPDATE_FEATURE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_FEATURE } from "../graphql/createFeature.mutation";

const log: any = LoggerModule?.getInstance();

export interface FeatureFormData {
  name?: string;
  alias?: string;
  featurePosition?: string | number;
  isMandatory?: boolean;
  isVisible?: boolean;
  isLabel?: boolean;
  isSingleAttribute?: boolean;
  isBase?: boolean;
  isCommon?: boolean;
  presentation?: string;
  isSingleSection?: boolean;
  section?: string | string[];
  sections?: string[];
  costumeType?: string | string[];
  [key: string]: any;
}

export interface UpdateFeatureProps {
  formData: FeatureFormData;
  id: string;
  presentation?: string;
  section?: string | string[];
  costumeType?: string | string[];
}

export interface CreateFeatureProps extends FeatureFormData {
  formData?: FeatureFormData;
  id?: string;
}

export interface DeleteFeatureProps {
  id: string;
  costumeTypeId?: string;
}

const FEATURE_FORM_FIELDS = [
  "name",
  "alias",
  "isLabel",
  "featurePosition",
  "isCommon",
  "isMandatory",
  "isVisible",
  "isSingleAttribute",
  "isBase",
  "isSingleSection",
  "sections",
  "description",
];

const FEATURE_PARENT_FIELDS = ["presentation", "costumeType", "section"];

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const trimIfString = (value: any) =>
  typeof value === "string" ? value.trim() : value;

const toArrayIds = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .map((item: any) => item?.id ?? item?._id ?? item)
      .filter(Boolean);
  }

  if (value) {
    return [value?.id ?? value?._id ?? value].filter(Boolean);
  }

  return [];
};

const getDisplayMessage = (err: any) => {
  const displayMessage =
    err?.graphQLErrors &&
    Array.isArray(err?.graphQLErrors) &&
    err?.graphQLErrors.find((item: any) => item.extensions);

  return displayMessage?.extensions?.display_message || err?.message;
};

const buildAllowedPayload = (args: any) => {
  const source = {
    ...(args?.formData ?? args ?? {}),
  };

  const isCommon = Boolean(source.isCommon ?? args?.isCommon);
  if (!isCommon) {
    delete source.sectionId;
  }

  FEATURE_PARENT_FIELDS.forEach((field) => {
    if (
      (source[field] === undefined ||
        source[field] === null ||
        source[field] === "") &&
      args?.[field] !== undefined
    ) {
      source[field] = args[field];
    }
  });

  const allowedFields = [...FEATURE_FORM_FIELDS, ...FEATURE_PARENT_FIELDS];

  return allowedFields.reduce((payload: any, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return payload;

    let value = source[field];

    if (field === "featurePosition") {
      if (value === undefined) return payload;
      if (value === "" || value === null) {
        payload[field] = null;
        return payload;
      }
      payload[field] = String(value);
      return payload;
    }

    if (field === "sections" || field === "section") {
      if (value === null) {
        payload.section = null;
        return payload;
      }

      const sectionIds = toArrayIds(value);

      if (sectionIds.length > 0) {
        payload.section = sectionIds;
      }

      return payload;
    }

    if (field === "costumeType") {
      const costumeTypeIds = toArrayIds(value);

      if (costumeTypeIds.length > 0) {
        payload.costumeType = costumeTypeIds;
      }

      return payload;
    }

    if (field === "presentation") {
      if (value !== undefined && value !== null && value !== "") {
        payload.presentation = value;
      }

      return payload;
    }

    if (value !== undefined) {
      payload[field] = field === "name" ? trimIfString(value) : value;
    }

    if (payload.description !== undefined) {
      payload.desc = payload.description;
      delete payload.description;
    }

    return payload;
  }, {});
};

export interface IFeatureMethods extends IPluginMethodMap {
  updateFeature(args: any): Promise<any>;
  createFeature(args: any): Promise<any>;
  deleteFeature(args: any): Promise<any>;
}

export class FeatureService implements IAgentPlugin {
  readonly methods: IFeatureMethods;

  constructor() {
    this.methods = {
      updateFeature: this.updateFeature.bind(this),
      createFeature: this.createFeature.bind(this),
      deleteFeature: this.deleteFeature.bind(this),
    };
  }

  async createFeature(args: CreateFeatureProps) {
    log?.blue?.("createFeature server component:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const data = buildAllowedPayload(args);

        const res = await client.mutate({
          mutation: CREATE_FEATURE,
          variables: { data },
          fetchPolicy: "no-cache",
        });

        return res?.data?.createCostumeFeature;
      }
    } catch (err: any) {
      throw Error(getDisplayMessage(err));
    }
  }

  async updateFeature(args: UpdateFeatureProps) {
    log?.blue?.("updateFeature server component:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const update = buildAllowedPayload(args);

        const res = await client.mutate({
          mutation: UPDATE_FEATURE,
          variables: {
            filter: {
              _id: args?.id,
            },
            update,
          },
          fetchPolicy: "no-cache",
        });

        return res?.data?.updateCostumeFeature;
      }
    } catch (err: any) {
      throw Error(getDisplayMessage(err));
    }
  }

  async deleteFeature(args: DeleteFeatureProps) {
    log?.blue?.("deleteFeature server component:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const variables: any = {
          filter: {
            _id: args?.id,
          },
        };

        if (args?.costumeTypeId) {
          variables.costumeTypeId = args.costumeTypeId;
        }

        const res = await client.mutate({
          mutation: DELETE_FEATURE,
          variables,
          fetchPolicy: "no-cache",
        });

        return res?.data;
      }
    } catch (err: any) {
      throw Error(getDisplayMessage(err));
    }
  }
}
